/**
 * Accounts receivable aging: net debit − credit per period bucket.
 * No invoice↔receipt matching (no reference / FIFO pairing).
 */

export const HEBREW_MONTHS = [
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
];

export const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * @param {object} tx
 * @param {boolean} filterByDocumentDate
 * @returns {Date|null}
 */
export const getTxBucketDate = (tx, filterByDocumentDate) => {
  if (filterByDocumentDate) {
    return parseDate(tx.documentDate) || parseDate(tx.registrationMonthDate) || parseDate(tx.uploadDate);
  }
  return parseDate(tx.registrationMonthDate) || parseDate(tx.documentDate) || parseDate(tx.uploadDate);
};

/**
 * Signed amount for the customer card: debit adds debt, credit reduces.
 */
export const getTxNetOnCard = (tx) => {
  const debit = Number(tx.debitAccount) || 0;
  const credit = Number(tx.creditAccount) || 0;
  return debit - credit;
};

/**
 * Opening signed balance from account-card payload (same convention as AccountCardReportHTML).
 */
export const getOpeningBalance = (accountCard) => {
  if (!accountCard) return 0;
  if (accountCard.initialDebit !== undefined || accountCard.initialCredit !== undefined) {
    return (Number(accountCard.initialDebit) || 0) - (Number(accountCard.initialCredit) || 0);
  }
  return Number(accountCard.balance) || 0;
};

const emptyBuckets = () => ({
  previous: 0,
  months: Array.from({ length: 12 }, () => 0),
  late: 0,
});

/**
 * Build one aging row for a customer account card.
 *
 * @param {object} accountCard - API accountCards[] item
 * @param {object} options
 * @param {number} options.reportYear
 * @param {boolean} options.filterByDocumentDate
 * @param {string} [options.phoneNumber]
 * @returns {object}
 */
export const buildAgingRow = (accountCard, { reportYear, filterByDocumentDate, phoneNumber = '' }) => {
  const buckets = emptyBuckets();
  const bucketTxs = {
    previous: [],
    months: Array.from({ length: 12 }, () => []),
    late: [],
  };

  const opening = getOpeningBalance(accountCard);
  if (opening !== 0) {
    buckets.previous = round2(buckets.previous + opening);
    bucketTxs.previous.push({
      id: `opening-${accountCard.accountCardId}`,
      description: accountCard.description || 'Initial Balance',
      type: opening >= 0 ? 'debit' : 'credit',
      debitAccount: opening > 0 ? opening : 0,
      creditAccount: opening < 0 ? Math.abs(opening) : 0,
      isOpening: true,
    });
  }

  const transactions = Array.isArray(accountCard.transactions) ? accountCard.transactions : [];
  transactions.forEach((tx) => {
    const net = getTxNetOnCard(tx);
    if (net === 0) return;
    const d = getTxBucketDate(tx, filterByDocumentDate);
    if (!d) {
      buckets.previous = round2(buckets.previous + net);
      bucketTxs.previous.push(tx);
      return;
    }
    const y = d.getFullYear();
    const m = d.getMonth();
    if (y < reportYear) {
      buckets.previous = round2(buckets.previous + net);
      bucketTxs.previous.push(tx);
    } else if (y > reportYear) {
      buckets.late = round2(buckets.late + net);
      bucketTxs.late.push(tx);
    } else {
      buckets.months[m] = round2(buckets.months[m] + net);
      bucketTxs.months[m].push(tx);
    }
  });

  const total = round2(
    buckets.previous + buckets.months.reduce((s, v) => s + v, 0) + buckets.late
  );

  return {
    accountCardId: accountCard.accountCardId || '',
    accountCardName: accountCard.accountCardName || '',
    phoneNumber: phoneNumber || '',
    previous: buckets.previous,
    months: buckets.months,
    late: buckets.late,
    total,
    bucketTxs,
    accountCard,
  };
};

/**
 * @param {object[]} accountCards
 * @param {object} options
 * @param {number} options.reportYear
 * @param {boolean} options.filterByDocumentDate
 * @param {Map<string, object>|Record<string, object>} [options.cardMasterById] - for phoneNumber
 * @param {boolean} [options.hideZeroTotal=true]
 */
export const buildAgingRows = (
  accountCards,
  { reportYear, filterByDocumentDate, cardMasterById, hideZeroTotal = true } = {}
) => {
  const master =
    cardMasterById instanceof Map
      ? cardMasterById
      : new Map(Object.entries(cardMasterById || {}));

  const rows = (accountCards || []).map((ac) => {
    const masterCard = master.get(String(ac.accountCardId)) || master.get(ac.accountCardId);
    const phone =
      masterCard?.phoneNumber ||
      masterCard?.phone ||
      ac.phoneNumber ||
      ac.phone ||
      '';
    return buildAgingRow(ac, { reportYear, filterByDocumentDate, phoneNumber: phone });
  });

  const filtered = hideZeroTotal ? rows.filter((r) => Math.abs(r.total) >= 0.005) : rows;

  filtered.sort((a, b) => {
    const idA = String(a.accountCardId);
    const idB = String(b.accountCardId);
    const nA = Number(idA);
    const nB = Number(idB);
    if (!Number.isNaN(nA) && !Number.isNaN(nB)) return nA - nB;
    return idA.localeCompare(idB, 'he');
  });

  return filtered;
};

export const sumAgingRows = (rows) => {
  const totals = emptyBuckets();
  let total = 0;
  (rows || []).forEach((r) => {
    totals.previous = round2(totals.previous + (r.previous || 0));
    r.months.forEach((v, i) => {
      totals.months[i] = round2(totals.months[i] + (v || 0));
    });
    totals.late = round2(totals.late + (r.late || 0));
    total = round2(total + (r.total || 0));
  });
  return { ...totals, total };
};

/** Display helper: blank string for ~0, otherwise 2-decimal number. */
export const formatAgingAmount = (value, { blankZero = true } = {}) => {
  const n = round2(value);
  if (blankZero && Math.abs(n) < 0.005) return '';
  return n.toFixed(2);
};
